import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { autoScrollViewHelper } from '../../../global/modules/autoScrollVewHelper';
import { useGuidedRegContext } from '../../context/guidedRegContext';
import { SAVE_FORM_DATA } from '../../context/reducer';
import { getAEMPersonalInfoData, navigateToPath } from '../utils/cashflowUtils';
import { useAnalyticsContext } from '../../../../config/GoogleTagManagerEvents';
import { EVENT_ECOMMERCE_NAMES_CONFIG } from '../../../../constants/analyticsConstants/Ecommerce';
import {
    STEP_NAMES,
    FIRST_NAME,
    LAST_NAME,
    DATE_OF_BIRTH,
    MOBILE_NUMBER,
    PASSWORD,
    TWELVE,
    ZERO,
    ACCOUNT_INFO,
    TWO,
    FOUR,
    FIVE,
    EIGHTEEN,
    THREE,
    EIGHT,
    MIN_AGE_LIMIT,
    ONE
} from '../constants';

/**
 * This function creates and manages the form state for the account information.
 *
 * @return {Object} An object containing the account state and various functions to handle form changes and validations.
 */
const usePersonalFormState = () => {
    const navigate = useNavigate();
    const intl = useIntl();
    const [isFormSubmit, setFormSubmit] = useState(false);
    const [autoScrolling, setAutoScrolling] = useState(false);
    const [validPassword, setValidPassword] = useState(false);
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [{ currentStepIndex, formData }, guidedRegDispatch] = useGuidedRegContext();
    const { firstName, lastName, dateOfBirth, mobileNumber, password } = formData[STEP_NAMES[0]]?.data || '';
    const startsOrEndsWithSpacesRegex = /^\s+|\s+$/;
    const validStringRegex = /^[a-zA-Z\s'-]*[a-zA-Z][a-zA-Z\s'-]*$/;
    const { commonConfig = {}, personalScreenConfig = {} } = getAEMPersonalInfoData() || {};
    const { continueButtonLabel = '' } = commonConfig || {};
    const { personalInfoTitle = '' } = personalScreenConfig || {};

    const { sendEventsForCreateCashForm, sendEventsForCreateCashFormError, sendEventsForCreateCashFormStepViewed } =
        useAnalyticsContext();
    const [formStarted, seFormStarted] = useState('');

    const translationValues = {
        firstName: intl.formatMessage({ id: 'enter_first_name' }),
        lastName: intl.formatMessage({ id: 'enter_last_name' }),
        validFirstName: intl.formatMessage({ id: 'valid_first_name' }),
        validLastName: intl.formatMessage({ id: 'valid_last_name' }),
        firstNameSpace: intl.formatMessage({ id: 'first_name_space' }),
        lastNameSpace: intl.formatMessage({ id: 'last_name_space' }),
        lastNameShort: intl.formatMessage({ id: 'last_name_short' }),
        password: intl.formatMessage({ id: 'enter_password' }),
        invalidPassword: intl.formatMessage({ id: 'invalid_password' }),
        mobileNumber: intl.formatMessage({ id: 'enter_mobile_number' }),
        validMobileNumber: intl.formatMessage({ id: 'valid_mobile_number' }),
        dob: intl.formatMessage({ id: 'enter_dob' }),
        validDob: intl.formatMessage({ id: 'valid_dob' })
    };

    const [personalState, setPersonalState] = useState({
        data: {
            [FIRST_NAME]: firstName,
            [LAST_NAME]: lastName,
            [MOBILE_NUMBER]: mobileNumber,
            [PASSWORD]: password,
            [DATE_OF_BIRTH]: dateOfBirth,
        },
        error: {
            [FIRST_NAME]: '',
            [LAST_NAME]: '',
            [MOBILE_NUMBER]: '',
            [PASSWORD]: '',
            [DATE_OF_BIRTH]: ''
        }
    });

    const validationRules = {
        [FIRST_NAME]: [
            {
                condition: value => value.trim() === '',
                message: translationValues.firstName
            },
            {
                condition: value => !validStringRegex.test(value),
                message: translationValues.validFirstName
            },
            {
                condition: value => startsOrEndsWithSpacesRegex.test(value),
                message: translationValues.firstNameSpace
            }
        ],
        [LAST_NAME]: [
            {
                condition: value => value.trim() === '',
                message: translationValues.lastName
            },
            {
                condition: value => value?.trim()?.length <= 1,
                message: translationValues.lastNameShort
            },
            {
                condition: value => !validStringRegex.test(value),
                message: translationValues.validLastName
            },
            {
                condition: value => startsOrEndsWithSpacesRegex.test(value),
                message: translationValues.lastNameSpace
            }
        ],
        [MOBILE_NUMBER]: [
            {
                condition: value => value.trim() === '',
                message: translationValues.mobileNumber
            },
            {
                condition: value => value.length < TWELVE,
                message: translationValues.validMobileNumber
            }
        ],
        [PASSWORD]: [
            {
                condition: value => value.trim() === '',
                message: translationValues.password
            },
            {
                condition: value => !validPassword,
                message: translationValues.invalidPassword
            }
        ],
        [DATE_OF_BIRTH]: [
            {
                condition: value => value === '',
                message: translationValues.dob
            },
            {
                condition: value => calculateAge(value) < MIN_AGE_LIMIT,
                message: translationValues.validDob
            }
        ]
    };

    const calculateAge = dateString => {
        const today = new Date();
        const birthDate = new Date(dateString);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDifference = today.getMonth() - birthDate.getMonth();
        // If birth month is later than current month OR
        // If it's the same month but birth day is later than today's day
        if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    const handleAutoScroll = () => {
        const filteredError = Object.fromEntries(
            Object.entries(personalState?.error || {}).filter(([key, value]) => value !== '')
        );
        autoScrollViewHelper(filteredError);
    };

    useEffect(() => {
        handleAutoScroll();
    }, [autoScrolling]);

    /**
     * Updates the account state with the provided form data.
     *
     * @param {string} name - the name of the form field
     * @param {any} value - the value of the form field
     * @return {void}
     */
    const saveFormDataState = (name, value) => {
        setPersonalState(prevState => ({
            ...prevState,
            data: {
                ...prevState.data,
                [name]: value
            },
            error: {
                ...prevState.error,
                [name]: ''
            }
        }));
    };

    /**
     * Saves the error state of a form field.
     *
     * @param {string} name - the name of the form field
     * @param {string} error - the error message
     * @return {void}
     */
    const saveFormErrorState = (name, error) => {
        setPersonalState(prevState => ({
            ...prevState,
            error: {
                ...prevState.error,
                [name]: error
            }
        }));
    };

    /**
     * Checks if the form is valid based on the provided validation rules.
     *
     * @return {boolean} true if the form has error, false if the form is valid
     */
    const isFormValid = () => {
        let isFormValid = true;
        for (const field in validationRules) {
            if (validationRules.hasOwnProperty(field)) {
                const rules = validationRules[field];
                for (const rule of rules) {
                    if (rule.condition(personalState.data[field])) {
                        saveFormErrorState(field, rule.message);
                        isFormValid = false;
                        sendAnalyticsForFieldError(field, rule.message);
                        break;
                    }
                }
            }
        }
        return isFormValid;
    };

    const sendAnalyticsForFieldError = (field, errorMessage) => {
        sendEventsForCreateCashFormError(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_FORM_ERROR,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CREATE_CASH_ACCOUNT,
            field,
            errorMessage,
            ONE
        );
    };

    const validateDate = e => {
        const value = e.target.value;
        const sanitizedValue = value.replace(/\D/g, '');
        let formattedDate;
        const dobLength = sanitizedValue.length;
        if (dobLength < THREE) formattedDate = sanitizedValue;
        else if (dobLength < FIVE) {
            formattedDate = `${sanitizedValue.slice(ZERO, TWO)}/${sanitizedValue.slice(TWO)}`;
        } else {
            formattedDate = `${sanitizedValue.slice(ZERO, TWO)}/${sanitizedValue.slice(
                TWO,
                FOUR
            )}/${sanitizedValue.slice(FOUR, EIGHT)}`;
        }
        e.target.value = formattedDate;
    };

    const handleDateChange = value => {
        saveFormDataState(DATE_OF_BIRTH, value);
        setUnsavedChanges(true);
    };
    /**
     * Handles the input change event.
     *
     * @param {Event} e - The input change event
     * @return {void}
     */
    const handleInputChange = e => {
        const { name = '', value = '' } = e?.target || {};
        saveFormDataState(name, value);
        setUnsavedChanges(true);
    };

    /**
     * Handle focus event for the specified field.
     *
     * @param {type} field - description of the field parameter
     * @return {type} description of the return value
     */
    const handleFocus = field => {
        saveFormErrorState(field, '');
        handleFieldInteraction(field);
    };

    /**
     * Handle the submit event.
     *
     * @param {type} e - the event object
     * @return {type} description of return value
     */
    const handleSubmit = e => {
        e.preventDefault();
        // your submit logic here
    };

    const saveDataInContext = () => {
        const payload = {
            step: STEP_NAMES[ZERO],
            data: {
                ...formData[STEP_NAMES[ZERO]]?.data,
                ...personalState.data
            }
        };
        guidedRegDispatch({ type: SAVE_FORM_DATA, payload });
    };

    /**
     * A function that handles the action when the continue button is clicked.
     */

    const onContinue = () => {
        setFormSubmit(true);
        setAutoScrolling(!autoScrolling);
        sendSubmittedAnalyticsEvent();
        if (isFormValid()) {
            sendAnalyticsOnFinalSubmit();      
            // saving the data in context
            saveDataInContext();
            navigateToPath(navigate, ACCOUNT_INFO);
        } else {
            handleAutoScroll();
        }
    };

    const sendAnalyticsOnFinalSubmit = () => {
        sendEventsForCreateCashFormStepViewed(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CCA_STEP_COMPLETED,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CREATE_CASH_ACCOUNT,
            personalInfoTitle,
            window.location.href,
            ONE
        );
    };

    const sendSubmittedAnalyticsEvent = () => {
        sendEventsForCreateCashForm(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_FIELD_FORM_SUBMITTED_EVENT,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CREATE_CASH_ACCOUNT,
            continueButtonLabel,
            ONE
        );
    };

    useEffect(() => {
        formStarted &&
            sendEventsForCreateCashForm(
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CCA_FORM_STARTED,
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CREATE_CASH_ACCOUNT,
                formStarted,
                ONE
            );
    }, [formStarted]);

    const handleFieldInteraction = fieldName => {
        !formStarted && seFormStarted(fieldName);
        sendEventsForCreateCashForm(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_FORM_INTERACTED,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CREATE_CASH_ACCOUNT,
            fieldName,
            ONE
        );
    };

    useEffect(() => {
        personalInfoTitle &&
            sendEventsForCreateCashFormStepViewed(
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CCA_FORM_VIEW_EVENT,
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CREATE_CASH_ACCOUNT,
                personalInfoTitle,
                window.location.href,
                ONE
            );
    }, [personalInfoTitle]);

    return {
        personalState,
        isFormSubmit,
        unsavedChanges,
        translationValues,
        handleInputChange,
        validateDate,
        handleDateChange,
        handleFocus,
        handleSubmit,
        setValidPassword,
        onContinue,
        validPassword
    };
};

export default usePersonalFormState;
